import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { structuralRowColorVariants, structuralRowTextVariants } from "./structuralRowStyles";
import { useTableContext } from "./TableContext";
import type { TableStructuralRowsColor, TableStructuralRowsText } from "./types";

export interface TableFooterRowProps {
  children?: ReactNode;
  className?: string;
  /** Overrides the table's `structuralRowsColor` for this row. */
  color?: TableStructuralRowsColor;
  /** Overrides the table's `structuralRowsText` for this row. */
  text?: TableStructuralRowsText;
}

/**
 * A structural row pinned under the body, inside the grid's border, for content that belongs to the
 * table rather than to a record — an add-row action, a totals line. Styled like the header through the
 * table's `structuralRowsColor` and `structuralRowsText` unless overridden here. For a status line
 * outside the grid use `Table.Footer` instead.
 */
export const TableFooterRow = ({ children, className, color, text }: TableFooterRowProps) => {
  const { structuralRowsColor, structuralRowsText } = useTableContext();

  return (
    <div
      data-slot="table-footer-row"
      className={cn(
        "flex min-h-12 shrink-0 items-center gap-2 border-t px-4 py-2",
        structuralRowColorVariants({ color: color ?? structuralRowsColor }),
        structuralRowTextVariants({ text: text ?? structuralRowsText }),
        className
      )}
    >
      {children}
    </div>
  );
};
